import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, of, ReplaySubject } from "rxjs";
import { map, shareReplay } from "rxjs/operators";
import { environment } from "../../environments/environment";
import { PagedList } from "../modules/shared/models/api-response.model";
import { AuthService } from "./auth.service";

const API_URL = environment.api;

export interface INotification {
    notificationId: number;
    authorId: number;
    title: string | null;
    description: string | null;
    notificationTypeId: number;
    displayOption: number;
    showOnDate: Date;
    stopOnDate: Date | null;
    createdAt: Date | null;
    updatedAt: Date | null;
    emailSentAt: Date | null;
    isDraft: boolean;
    isDeleted: boolean;
    accountIds: string[];
    roleIds: number[];
    authorAvatar: string | null;
    authorName: string | null;
}

export class NotificationType {
    notificationTypeId: number;
    name: string;
}

export interface IUserNotification {
    notificationId: number;
    notificationType: string;
    displayOption: number;
    title: string;
    description: string;
    authorName: string;
    authorId: number;
    authorAvatar: string;
    showOnDate: Date;
    stopOnDate: Date;
    audience: string;
    readOnDate: Date | null;
}

interface IUserNotificationBundle {
    notifications: IUserNotification[];
    avatars: { [userId: number]: string };
}

@Injectable()
export class NotificationService {
    unreadNotificationCount$ = new ReplaySubject<number[]>(1);

    private startOfDay = (d: Date) =>
        new Date(d.setHours(0, 0, 0)).toISOString();
    private endOfDay = (d: Date) =>
        new Date(d.setHours(23, 59, 59)).toISOString();

    constructor(
        private http: HttpClient,
        private authService: AuthService
    ) {}

    getUserModals(): Observable<IUserNotification[]> {
        if (!this.isImpersonating) {
            return this.http
                .get<
                    IUserNotification[]
                >(this.buildUrl(`notifications/user/modals`), { headers: this.createHeaders() })
                .pipe(
                    map((res) => res.map((un) => this.mapUserNotification(un)))
                );
        } else {
            return of([]);
        }
    }

    getUserNotifications(
        filter: string | null = null,
        includeSent = false,
        dateRange: Partial<{ begin: Date; end: Date }> | null = null,
        notificationTypes: number[] | null = null
    ): Observable<IUserNotification[]> {
        const p: any = { includeSent };

        if (filter) {
            p.filter = filter;
        }

        if (notificationTypes) {
            p["notification-types"] = notificationTypes.join(",");
        }

        if (dateRange !== null && dateRange.begin && dateRange.end) {
            p["start-at"] = this.startOfDay(dateRange.begin);
            p["end-at"] = this.endOfDay(dateRange.end);
        } else {
            let start = new Date();
            start = new Date(start.setDate(start.getDate() - 30));

            p["start-at"] = this.startOfDay(start);
            p["end-at"] = this.endOfDay(new Date());
        }

        const options = { headers: this.createHeaders(), params: p };

        return this.http
            .get<IUserNotificationBundle>(
                this.buildUrl(`notifications/user`),
                options
            )
            .pipe(map((res) => this.mapReduceUserNotificationBundle(res)));
    }

    markAsRead(notificationIds: number[]): Observable<any> {
        if (!this.isImpersonating) {
            return this.http.patch(
                this.buildUrl(`notifications/user/read`),
                notificationIds,
                {
                    headers: this.createHeaders()
                }
            );
        } else {
            return of();
        }
    }

    get isImpersonating(): boolean {
        return window.localStorage.getItem("original_access_token") ?
                true
            :   false;
    }

    fetchUnreadIds() {
        this.http
            .get<number[]>(this.buildUrl(`notifications/user/unreadIds`), {
                headers: this.createHeaders()
            })
            .pipe(map((res) => this.unreadNotificationCount$.next(res)))
            .subscribe();
    }

    getNotification(notificationId: number): Observable<INotification> {
        return this.http
            .get<INotification>(
                this.buildUrl(`notifications/${notificationId}`),
                { headers: this.createHeaders() }
            )
            .pipe(map((res) => this.mapNotification(res)));
    }

    getAll(
        offset: number,
        limit: number,
        sort: string | null = "+notificationId",
        filter: string | null = null,
        includeSent: boolean,
        dateRange: Partial<{ begin: Date; end: Date }> | null,
        notificationTypes: number[] | null = null,
        displayTypes: number[] | null = null,
        draftStatuses: string[] | null = null
    ): Observable<PagedList<INotification>> {
        const p = {
            includeSent
        } as any;

        if (limit !== -1) {
            p.offset = `${offset * limit}`;
            p.limit = limit.toString();
        }

        if (sort) {
            if (sort.charAt(0) === "+") {
                sort = sort.substr(1, sort.length);
            }

            p.sort = sort;
        }

        if (filter) {
            p.filter = filter;
        }

        if (notificationTypes) {
            p["notification-types"] = notificationTypes.join(",");
        }

        if (displayTypes) {
            p["display-types"] = displayTypes.join(",");
        }

        if (draftStatuses) {
            p["draft-statuses"] = draftStatuses.join(",");
        }

        if (dateRange !== null && dateRange.begin && dateRange.end) {
            p["start-at"] = this.startOfDay(dateRange.begin);
            p["end-at"] = this.endOfDay(dateRange.end);
        }

        const options = { headers: this.createHeaders(), params: p };

        return this.http
            .get(this.buildUrl("notifications"), options)
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    res.offset = offset;
                    res.data = res.data.map((n: INotification) =>
                        this.mapNotification(n)
                    );

                    return res;
                })
            );
    }

    createNotification(notification: INotification): Observable<INotification> {
        if (!notification.stopOnDate) {
            const d = new Date(notification.showOnDate);
            notification.stopOnDate = new Date(d.setDate(d.getDate() + 30));
        }

        return this.http
            .post<INotification>(this.buildUrl("notifications"), notification, {
                headers: this.createHeaders()
            })
            .pipe(map((res) => this.mapNotification(res)));
    }

    deleteNotification(notificationId: number): Observable<any> {
        return this.http
            .delete(this.buildUrl(`notifications/${notificationId}`), {
                headers: this.createHeaders()
            })
            .pipe(shareReplay());
    }

    updateNotification(notification: INotification): Observable<INotification> {
        const url = this.buildUrl(
            `notifications/${notification.notificationId}`
        );

        if (!notification.stopOnDate) {
            const d = new Date(notification.showOnDate);
            notification.stopOnDate = new Date(d.setDate(d.getDate() + 30));
        }

        return this.http
            .put<INotification>(url, notification, {
                headers: this.createHeaders()
            })
            .pipe(map((res) => this.mapNotification(res)));
    }

    getNotificationTypes(): Observable<NotificationType[]> {
        return this.http.get<NotificationType[]>(
            this.buildUrl("notifications/types"),
            {
                headers: this.createHeaders()
            }
        );
    }

    private buildUrl(urlFragment: string) {
        return `${API_URL}/api/v1/${urlFragment}`;
    }

    private createHeaders(): HttpHeaders {
        const h = new HttpHeaders({
            Authorization: `bearer ${this.authService.accessToken}`,
            "Content-Type": "application/json"
        });

        return h;
    }

    private mapNotification(n: any): INotification {
        n.showOnDate = new Date(n.showOnDate);

        if (n.createdAt) {
            n.createdAt = new Date(n.createdAt);
        }

        if (n.updatedAt) {
            n.updatedAt = new Date(n.updatedAt);
        }

        if (n.stopOnDate) {
            n.stopOnDate = new Date(n.stopOnDate);
        }

        if (n.emailSentAt) {
            n.emailSentAt = new Date(n.emailSentAt);
        }

        return n;
    }

    private mapReduceUserNotificationBundle(
        bundle: IUserNotificationBundle
    ): IUserNotification[] {
        const notifications = bundle.notifications.map((n) =>
            this.mapUserNotification(n)
        );

        notifications.forEach(
            (n) => (n.authorAvatar = bundle.avatars[n.authorId])
        );

        return notifications;
    }

    private mapUserNotification(un: any): IUserNotification {
        un.showOnDate = new Date(un.showOnDate);
        un.stopOnDate = new Date(un.stopOnDate);

        return un;
    }
}
